// src/App.js
import React, { useEffect, useState } from 'react';
import './setupAxios';
import { BrowserRouter as Router, Routes, Route, useParams, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import About from './pages/About';
import './index.css';
import 'bootstrap/dist/css/bootstrap.min.css'; // Default Bootstrap
import ContactUs from './pages/ContactUs';
import Doctors from './pages/Doctors';
import Login from './pages/admin/Login';
import DoctorList from './pages/admin/DoctorList';
import DoctorPage from './pages/admin/DoctorPage';
import CreateDoctor from './pages/admin/CreateDoctor';
import MyDoctorPage from './pages/admin/MyDoctorPage';
import PatientList from './pages/admin/PatientList';
import CreatePatient from './pages/admin/CreatePatient';
import EditPatient from './pages/admin/EditPatient';
import PatientDetails from './pages/admin/PatientDetails';
import PatientMessages from './pages/admin/PatientMessages';
import MessagesOverview from './pages/admin/MessagesOverview';
import { NavProvider } from './components/NavContext';
import OrderList from './pages/admin/OrderList';
import AppointmentsList from './pages/admin/AppointmentsList';
import NotesSearch from './pages/admin/NotesSearch';
import DoctorInformation from './pages/admin/DoctorInformation';
import TestInformation from './pages/admin/TestInformation';
import AssistantManagement from './pages/admin/AssistantManagement';
import DoctorStats from './pages/admin/DoctorStats';
import Downloads from './pages/news/Downloads';
import ClinicLocationInfo from './pages/news/ClinicLocationInfo';
import AppointmentModalUpdate from './pages/news/AppointmentModalUpdate';
import PageTitle from './components/PageTitle';
import PrintSettings from './pages/admin/PrintSettings';
import MessageTemplates from './pages/admin/MessageTemplates';
import Privacy from './pages/Privacy';
import Support from './pages/Support';
import Registration from './pages/Registration';
import AdminNoteReviser from './pages/admin/AdminNoteReviser';
import AdminOnlineNotes from './pages/admin/AdminOnlineNotes';
import DoctorPublicList from './pages/admin/DoctorPublicList';
import DoctorMetrics from './pages/admin/DoctorMetrics';
import LocationReports from './pages/admin/LocationReports';
import UserLocationsList from './pages/admin/UserLocationsList';
import BroadcastNotification from './pages/admin/BroadcastNotification';
import LocationEditor from './pages/admin/LocationEditor';

function App() {

  useEffect(() => {
    window.addEventListener('deviceId', (e) => {
      localStorage.setItem('device_id', e.detail.deviceId);
    });

    // Fallback in case event was dispatched before listener
    if (window.AndroidDevice && window.AndroidDevice.getDeviceId) {
      const deviceId = window.AndroidDevice.getDeviceId();
      const event = new CustomEvent('deviceId', { detail: { deviceId } });
      window.dispatchEvent(event);
    } else if (window.__DEVICE_ID__) {
      const event = new CustomEvent('deviceId', { detail: { deviceId: window.__DEVICE_ID__.deviceId } });
      window.dispatchEvent(event);
    }
  }, []);

  return (
    <HelmetProvider>
      <NavProvider>
        <Router>
          <AppContent />
        </Router>
      </NavProvider>
    </HelmetProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const [, forceAuthRerender] = useState(0);

  useEffect(() => {
    const onChange = () => forceAuthRerender((v) => v + 1);
    window.addEventListener('accountSessionsChanged', onChange);
    return () => window.removeEventListener('accountSessionsChanged', onChange);
  }, []);

  const userInfoRaw = localStorage.getItem('userInfo');
  const isLoggedIn = !!userInfoRaw;
  const isLoginPage = location.pathname === '/control/login' || (location.pathname === '/control/' && !isLoggedIn);
  const isMessagesPage = location.pathname.includes('/control/patients/') && location.pathname.includes('/messages');
  // Determine default element based on role when logged in (match Login.js behavior)
  let role = null;
  try {
    role = userInfoRaw ? JSON.parse(userInfoRaw).role : null;
  } catch (_) {}
  const defaultElement = isLoggedIn
    ? (role === 'admin'
        ? <DoctorListWithTitle />
        : role === 'test'
          ? <TestInformationWithTitle />
          : <AppointmentsListWithTitle />)
    : <Navigate to="/control/login" replace />;

  return (
    <div>
      {!isLoginPage && <Navbar />}
      <main className={isLoginPage ? "login-content" : "content"}>
        <Routes>
          <Route path="/control/" element={defaultElement} />
          <Route path="/control/about" element={<AboutWithTitle />} />
          <Route path="/control/contact-us" element={<ContactUsWithTitle />} />
          <Route path="/control/doctors" element={<RequireAuth><DoctorsWithTitle /></RequireAuth>} />
          <Route path="/control/login" element={<LoginWithTitle />} />
          <Route path="/control/doctor-list" element={<RequireAuth><DoctorListWithTitle /></RequireAuth>} />
          <Route path="/control/doctor-create" element={<RequireAuth><CreateDoctorWithTitle /></RequireAuth>} />
          <Route path="/control/doctor/:doctorId" element={<RequireAuth><DoctorWrapper /></RequireAuth>} />
          <Route path="/control/my-doctor" element={<RequireAuth><MyDoctorPageWithTitle /></RequireAuth>} />
          <Route path="/control/order-list" element={<RequireAuth><OrderListWithTitle /></RequireAuth>} />
          <Route path="/control/patients/:id/messages" element={<RequireAuth><PatientMessages /></RequireAuth>} />
          <Route path="/control/messages" element={<RequireAuth><MessagesOverviewWithTitle /></RequireAuth>} />
          <Route path="/control/patients" element={<RequireAuth><PatientListWithTitle /></RequireAuth>} />
          <Route path="/control/patient-create" element={<RequireAuth><CreatePatientWithTitle /></RequireAuth>} />
          <Route path="/control/patient/:id" element={<RequireAuth><PatientDetailsWithTitle /></RequireAuth>} />
          <Route path="/control/patient/:id/edit" element={<RequireAuth><EditPatientWithTitle /></RequireAuth>} />
          <Route path="/control/appointments" element={<RequireAuth><AppointmentsListWithTitle /></RequireAuth>} />
          <Route path="/control/notes-search" element={<RequireAuth><NotesSearchWithTitle /></RequireAuth>} />
          <Route path="/control/doctor-information" element={<RequireAuth><DoctorInformationWithTitle /></RequireAuth>} />
          <Route path="/control/test-information" element={<RequireAuth><TestInformationWithTitle /></RequireAuth>} />
          <Route path="/control/admin/assistants" element={<RequireAuth><AssistantManagementWithTitle /></RequireAuth>} />
          <Route path="/control/downloads" element={<DownloadsWithTitle />} />
          <Route path="/control/clinic-location-info" element={<RequireAuth><ClinicLocationInfoWithTitle /></RequireAuth>} />
          <Route path="/control/appointment-modal-update" element={<RequireAuth><AppointmentModalUpdateWithTitle /></RequireAuth>} />
          <Route path="/control/stats" element={<RequireAuth><DoctorStatsWithTitle /></RequireAuth>} />
          <Route path="/control/print-settings" element={<RequireAuth><PrintSettingsWithTitle /></RequireAuth>} />
          <Route path="/control/message-templates" element={<RequireAuth><MessageTemplatesWithTitle /></RequireAuth>} />
          <Route path="/control/admin/reviser" element={<RequireAuth><AdminNoteReviserWithTitle /></RequireAuth>} />
          <Route path="/control/admin/online-notes" element={<RequireAuth><AdminOnlineNotesWithTitle /></RequireAuth>} />
          <Route path="/control/admin/doctor-public-list" element={<RequireAuth><DoctorPublicListWithTitle /></RequireAuth>} />
          <Route path="/control/admin/metrics" element={<RequireAuth><DoctorMetricsWithTitle /></RequireAuth>} />
          <Route path="/control/admin/location-reports" element={<RequireAuth><LocationReportsWithTitle /></RequireAuth>} />
          <Route path="/control/admin/user-locations" element={<RequireAuth><UserLocationsListWithTitle /></RequireAuth>} />
          <Route path="/control/admin/broadcast" element={<RequireAuth><BroadcastNotificationWithTitle /></RequireAuth>} />
          <Route path="/control/admin/location-editor" element={<RequireAuth><LocationEditorWithTitle /></RequireAuth>} />
          <Route path="/control/privacy" element={<PrivacyWithTitle />} />
          <Route path="/control/support" element={<SupportWithTitle />} />
          <Route path="/control/registration" element={<RegistrationWithTitle />} />
        </Routes>
      </main>
      {!isLoginPage && !isMessagesPage && <Footer />}
    </div>
  );
}

function DoctorWrapper() {
  const { doctorId } = useParams();
  return (
    <>
      <PageTitle title="Doctor Details" />
      <DoctorPage doctorId={doctorId} />
    </>
  );
}

// Wrapper components with page titles
function LoginWithTitle() {
  return <><PageTitle title="Login" /><Login /></>;
}

function AboutWithTitle() {
  return <><PageTitle title="About Us" /><About /></>;
}

function ContactUsWithTitle() {
  return <><PageTitle title="Contact Us" /><ContactUs /></>;
}

function DoctorsWithTitle() {
  return <><PageTitle title="Our Doctors" /><Doctors /></>;
}

function PrivacyWithTitle() {
  return <><PageTitle title="Privacy" /><Privacy /></>;
}

function SupportWithTitle() {
  return <><PageTitle title="Support" /><Support /></>;
}

function RegistrationWithTitle() {
  return <><PageTitle title="Registration" /><Registration /></>;
}

function DoctorListWithTitle() {
  return <><PageTitle title="Doctor List" /><DoctorList /></>;
}

function CreateDoctorWithTitle() {
  return <><PageTitle title="Add New Doctor" /><CreateDoctor /></>;
}

function MyDoctorPageWithTitle() {
  return <><PageTitle title="My Doctor" /><MyDoctorPage /></>;
}

function OrderListWithTitle() {
  return <><PageTitle title="Orders" /><OrderList /></>;
}

function PatientListWithTitle() {
  return <><PageTitle title="Patients" /><PatientList /></>;
}

function CreatePatientWithTitle() {
  return <><PageTitle title="Add New Patient" /><CreatePatient /></>;
}

function PatientDetailsWithTitle() {
  return <><PageTitle title="Patient Details" /><PatientDetails /></>;
}

function EditPatientWithTitle() {
  return <><PageTitle title="Edit Patient" /><EditPatient /></>;
}

function AppointmentsListWithTitle() {
  return <><PageTitle title="Appointments" /><AppointmentsList /></>;
}

function NotesSearchWithTitle() {
  return <><PageTitle title="Notes Search" /><NotesSearch /></>;
}

function DoctorInformationWithTitle() {
  return <><PageTitle title="Profile" /><DoctorInformation /></>;
}

function TestInformationWithTitle() {
  return <><PageTitle title="Profile (Test)" /><TestInformation /></>;
}

function AssistantManagementWithTitle() {
  return <><PageTitle title="Assistant Management" /><AssistantManagement /></>;
}

function DownloadsWithTitle() {
  return <><PageTitle title="Downloads" /><Downloads /></>;
}

function ClinicLocationInfoWithTitle() {
  return <><PageTitle title="Clinic Location Feature" /><ClinicLocationInfo /></>;
}

function AppointmentModalUpdateWithTitle() {
  return <><PageTitle title="Appointment Modal Update" /><AppointmentModalUpdate /></>;
}

function DoctorStatsWithTitle() {
  return <><PageTitle title="Statistics" /><DoctorStats /></>;
}

function PrintSettingsWithTitle() {
  return <><PageTitle title="Print Settings" /><PrintSettings /></>;
}

function MessageTemplatesWithTitle() {
  return <><PageTitle title="Message Templates" /><MessageTemplates /></>;
}

function AdminNoteReviserWithTitle() {
  return <><PageTitle title="AI Reviser (Admin)" /><AdminNoteReviser /></>;
}

function AdminOnlineNotesWithTitle() {
  return <><PageTitle title="Online Booked Notes (Admin)" /><AdminOnlineNotes /></>;
}

function DoctorPublicListWithTitle() {
  return <><PageTitle title="قائمة الأطباء للنشر" /><DoctorPublicList /></>;
}

function DoctorMetricsWithTitle() {
  return <><PageTitle title="Doctor Metrics" /><DoctorMetrics /></>;
}

function LocationReportsWithTitle() {
  return <><PageTitle title="Location Reports" /><LocationReports /></>;
}

function UserLocationsListWithTitle() {
  return <><PageTitle title="User Locations" /><UserLocationsList /></>;
}

function BroadcastNotificationWithTitle() {
  return <><PageTitle title="Broadcast Notification" /><BroadcastNotification /></>;
}

function LocationEditorWithTitle() {
  return <><PageTitle title="Location Editor" /><LocationEditor /></>;
}

function MessagesOverviewWithTitle() {
  return <><PageTitle title="Messages" /><MessagesOverview /></>;
}

export default App;

// Auth guard component
function RequireAuth({ children }) {
  const [, forceAuthRerender] = useState(0);
  useEffect(() => {
    const onChange = () => forceAuthRerender((v) => v + 1);
    window.addEventListener('accountSessionsChanged', onChange);
    return () => window.removeEventListener('accountSessionsChanged', onChange);
  }, []);

  const token = localStorage.getItem('userInfo');
  const location = useLocation();
  if (!token) {
    return <Navigate to="/control/login" state={{ from: location }} replace />;
  }
  return children;
}
